<template>
  <PageHeader :showBreadcrumb="true" />
  <p>
    Enter text in the search input to find a value that matches or partially matches.
  </p>
  <p>
    Click the column headers to sort the table data ascending then again for descending
  </p>
  <div>
    <DataTable
      ref="documentManagementTable"
      v-model:filters="filters"
      :value="documents"
      :dataKey="doc => `${doc.id}-${doc.version}`"
      stripedRows
      :loading="loading"
      paginator
      :rows="25"
      sortField="createdAt"
      :sortOrder="-1"
      show-gridlines
    >
      <template #header>
        <div class="header-components gap-3">
          <IconField>
            <InputText v-model="filters['global'].value" label="Keyword Search" aria-label="Keyword Search" placeholder="Keyword Search" />
            <InputIcon>
              <i class="pi pi-search" ></i>
            </InputIcon>
          </IconField>
          <Button icon="pi pi-external-link" rounded label="Export" @click="exportCSV($event)" />
        </div>
      </template>

      <template #empty>
        No Data.
      </template>

      <Column field="id" header="ID" :sortable="true">
        <template #body="slotProps">
          {{ slotProps.data.id }}.{{ slotProps.data.version }}
        </template>
      </Column>

      <Column field="createdAt" header="Date" :sortable="true">
        <template #body="slotProps">
          {{ new Date(slotProps.data.createdAt).toLocaleString() }}
        </template>
      </Column>
      <Column field="moduleType" header="Module" :sortable="true" />
      <Column field="categoryOfUpload" header="Category" :sortable="true" />
      <Column field="uploaderName" header="Uploader" :sortable="true" />
      <Column field="entityName" header="Entity" :sortable="true" />
      <Column field="networkName" header="Network" :sortable="true" />
      <Column field="networkId" header="Network ID" :sortable="true" />
      <Column field="creatorFullName" header="Creator" :sortable="true" />
      <Column field="creatorEmail" header="Email" :sortable="true" />

      <Column header="File Download" :exportable="false">
        <template #body="slotProps">
          <a style="cursor:pointer;" @click="downloadThisFile(slotProps.data)">
            {{ slotProps.data.documentName }}
          </a>
        </template>
      </Column>

      <Column field="isDeleted" header="Deleted" :sortable="true">
        <template #body="slotProps">
          {{ slotProps.data.isDeleted ? 'Yes' : 'No' }}
        </template>
      </Column>

      <Column header="Document Approved" field="documentApproved">
        <template #body="slotProps">
          <Checkbox
            v-model="slotProps.data.documentApproved"
            @change="() => toggleApproved(slotProps.data)"
            :aria-label="`Document approval for ${slotProps.data.id}.${slotProps.data.version}`"
            binary
          />
        </template>
      </Column>

      <Column header="Actions" :exportable="false">
        <template #body="slotProps">
          <div class="action-container">
            <Button
              class="btn btn-success nowrap-btn"
              label="Upload"
              icon="pi pi-upload"
              @click="toggleUploaderDialog(slotProps.data)"
              rounded
            />
            <Button
              class="btn btn-success nowrap-btn"
              label="Notes"
              icon="pi pi-pen-to-square"
              @click="toggleNotesDialog(slotProps.data)"
              rounded
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog
      v-if="documentSelected"
      :visible="isUploaderOpen"
      modal
      header="Upload Document"
      :closable="false"
      :style="{ width: '40rem' }"
      @hide="dialogStore.close()"
    >
      <div class="row mb-4">
        <div class="col-12">
          <MNFileUpload
            module-type="Document Management Table"
            :index="documentSelected?.id"
            :label="documentSelected?.label"
            :ref="documentSelected?.refName"
            :name="documentSelected?.refName"
            :accept="documentSelected?.acceptedFileTypes"
            url="/undefined"
            :entity="documentSelected?.entityUniqueId"
            :network="documentSelected?.networkUniqueId"
          />
        </div>
      </div>

      <template #footer>
        <div class="d-flex justify-content-end">
          <Button
            type="button"
            rounded
            raised
            label="Close"
            severity="secondary"
            @click="dialogStore.close()"
          />
        </div>
      </template>
    </Dialog>

    <Dialog
      v-if="documentSelected"
      :visible="isNotesOpen"
      modal
      header="Document Notes"
      :closable="false"
      :style="{ width: '40rem' }"
      @hide="dialogStore.close()"
    >
      <Notes
        :parentType="NOTE_TYPES.Document"
        :parentId="documentSelected.id"
        :parentVersion="documentSelected.version"
        @close="dialogStore.close()"
        @updated="refreshDocuments"
      />

      <template #footer>
        <div class="d-flex justify-content-end">
          <Button
            type="button"
            rounded
            raised
            label="Close"
            severity="secondary"
            @click="dialogStore.close();"
          />
        </div>
      </template>
    </Dialog>
  </div>

</template>
<script setup>
import { ref, computed, onMounted } from "vue";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";
import PageHeader from "@/components/common/PageHeader.vue";
import { useAuthStore } from "@/stores/authStore.js";
import { getDocuments, saveEditDocument } from "@/services/documentDBservice.js";
import { getPreSignedDownloadUrl } from "@/services/downloadFile.js";
import Notes from "@/components/notes/Notes.vue";
import MNFileUpload from "@/components/common/MNFileUpload.vue";
import {NOTE_TYPES} from "@/utils/constants.js";
import {useToast} from "primevue/usetoast";
import {useDialogStore} from "@/stores/dialogStore.js";
import {FilterMatchMode} from "@primevue/core/api";

const toast = useToast();
const authStore = useAuthStore();
const dialogStore = useDialogStore();

const documents = ref([]);
const documentSelected = ref(null);

// Table
const documentManagementTable = ref();
const loading = ref(true);
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const exportCSV = () => {
  documentManagementTable.value.exportCSV();
};

// Dialog Controls
const isUploaderOpen = computed(() => dialogStore.isOpen('documentManagementUploader'));
const isNotesOpen = computed(() => dialogStore.isOpen('documentManagementNotes'));

const refreshDocuments = async () => {
  loading.value = true;
  documents.value = await getDocuments();
  loading.value = false;
};

onMounted(async () => {
  await refreshDocuments();
});

// Download file
const downloadThisFile = async (doc) => {
  documentSelected.value = doc;
  const response = await getPreSignedDownloadUrl(
      documentSelected.value.documentName
  );
  window.open(response.url);
};

// Open/close uploader dialog
const toggleUploaderDialog = async (doc) => {
  documentSelected.value = doc;
  dialogStore.toggle('documentManagementUploader');
  if (!isUploaderOpen.value) {
    await refreshDocuments();
  }
};


// Open/close notes dialog
const toggleNotesDialog = async (doc) => {
  documentSelected.value = doc;
  dialogStore.toggle('documentManagementNotes');
};

// Toggle approval
const toggleApproved = async doc => {
  // documentApproved is already updated by v-model
  doc.editorFullName = authStore.user.fullName;
  doc.editorEmail = authStore.user.email;

  try {
    await saveEditDocument(doc);
    await refreshDocuments();
  } catch (error) {
    console.error('Error updating document approval:', error);
  }
};


</script>

<style scoped>
label {
  display: block;
  margin-bottom: 10px;
}

.header-components {
  display: flex;
  justify-content: flex-end;
}

.action-container {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  align-items: center;
}

.nowrap-btn {
  white-space: nowrap;
}
</style>
